/**
 * COMPTABILITÉ — IGNORER LA CLÔTURE D'UNE NUIT.
 *
 * Gestion n'était pas encore en service pour les premières nuits suivies :
 * leur clôture détaillée ne sera jamais faite. On l'IGNORE, en validant en
 * bloc le chiffre d'affaires total et le nombre d'entrées que Gestion propose
 * — l'exploitant peut corriger l'un et l'autre avant de valider. La nuit
 * n'est plus « à faire » ; son chiffre compte dans le chiffre d'affaires, à
 * part des nuits clôturées.
 *
 * `IgnorerCloture` est le SEUL point d'entrée (contrat du 07/10/2026) : la
 * fiche de nuit l'affiche dans son en-tête, l'assistant dans son bandeau
 * (`compact`, un bouton du même gabarit que « Fiche de la nuit »). Le
 * composant lit lui-même la proposition : il sait seul s'il a quelque chose à
 * offrir (une nuit à venir, en cours ou déjà clôturée ne s'ignore pas — il ne
 * rend alors rien), et ce qu'il doit dire d'une nuit déjà validée.
 *
 * ═══ CE QUE LA MODALE MONTRE, ET DANS QUEL ORDRE ═══
 *
 * La conséquence d'abord (la nuit sort des nuits à traiter), puis ce que
 * Gestion propose, détaillé : bars et vestiaire selon ZAP, ventes Shotgun
 * frais Shotgun déduits, d'après les redditions (total − frais gardés), total ; billets Shotgun et guestlist, total.
 * Ensuite seulement les deux champs, et un motif facultatif. Un montant
 * inconnu s'écrit « — », jamais 0.
 *
 * ═══ PRÉREMPLI SEULEMENT QUAND LE TOTAL EST COMPLET ═══
 *
 * Le champ « Chiffre d'affaires validé » entre dans le retenu du mois. Il
 * n'est prérempli que si le cœur rend un total (chaque part connue) ; sinon
 * il reste vide, et l'aide dit d'après quoi le saisir. La valeur faciale des
 * billets n'est JAMAIS un chiffre d'affaires : quand le cœur la donne, c'est
 * un repère, posé HORS du bloc et du total, sous ce nom.
 *
 * ═══ LES DROITS ═══
 *
 * Valider = `finance.closure.close`, annuler = `finance.closure.revise`, les
 * deux avec ré-authentification forte : `adminMutate` rejoue la requête après
 * le `STEPUP_REQUIRED`, rien d'autre à câbler ici.
 */

import { useCallback, useEffect, useState } from 'react'
import { BadgeCheck, History, RotateCcw, X } from 'lucide-react'
import { Modal, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { formatMoney } from './money.js'
import { MoneyInput, MotifField, lireJson, motifSuffisant, nuitLongue, objetSous, type Etat } from './ClosingCommon.js'
import { ListeEntrees, nombre, type EntreesNuit } from './entrees.js'
import { REPRIS_COURT, phraseReprise, repriseDe } from './reprise-historique.js'
import './finance-entrees.css'

export interface IgnorerClotureProps {
  /** La nuit commerciale, AAAA-MM-JJ. */
  date: string
  /** Appelé après une validation (ou une annulation) réussie. */
  onDone: () => void
  /** Variante discrète (bouton fantôme, pour un en-tête chargé). */
  compact?: boolean
}

/* ═══════════════════════ ce que l'API rend ═══════════════════════ */

/** Miroir de `GET /api/v1/finance/nights/:date/validation-proposal`. */
export interface PropositionValidation {
  business_date: string
  blocked_reason: string | null
  /** `GUICHET_HORS_NUIT` : une vente du guichet d'une autre nuit attend sa décision — dit AVANT toute saisie. */
  blocked_code: 'A_VENIR' | 'EN_COURS' | 'CLOTUREE' | 'REVISION_EN_COURS' | 'DEJA_VALIDEE' | 'GUICHET_HORS_NUIT' | null
  revenue: {
    /** Faux = la session ne lit pas les montants : ils valent tous `null`. */
    amounts_visible: boolean
    zap_cents: number | null
    zap_offers_cents: number | null
    zap_reports: number
    ticketing_cents: number | null
    /** `null` = une part manque (une soirée sans reddition lue) : les notes disent laquelle. */
    ticketing_source: 'REDDITION' | 'BILLETS' | 'REDDITION_ET_BILLETS' | 'SANS_BILLET' | null
    reddition_refs: string[]
    /** REPÈRE, jamais un chiffre d'affaires : valeur faciale des billets, hors de tout total. */
    facial_cents: number | null
    /**
     * La billetterie des associations (Pretix) dans le total proposé, parts
     * déduites (16/10/2026) ; `null` = réglage éteint, ou montant réservé.
     * Absente d'un serveur d'avant.
     */
    associations_cents?: number | null
    /** REPÈRE : la valeur faciale des billets Pretix vendus. */
    associations_face_cents?: number | null
    associations_sold?: number
    /** `null` = au moins une part manque : rien n'est prérempli. */
    total_cents: number | null
    notes: string[]
  }
  entries: EntreesNuit & { closing_id: string | null }
  current: {
    revenue_cents: number | null
    entries: number
    validated_by_name: string | null
    validated_at: string
    reason: string | null
    proposed_revenue_cents: number | null
    proposed_entries: number | null
    /** Nuit reprise de l'historique ; absent d'un serveur d'avant le 16/10/2026. */
    history?: unknown
  } | null
}

function commeProposition(v: unknown): PropositionValidation | null {
  const o = objetSous(v)
  if (!o || typeof o['business_date'] !== 'string' || !objetSous(o['revenue']) || !objetSous(o['entries'])) return null
  return o as unknown as PropositionValidation
}

const QUAND = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })

/** « le 7 octobre 2026 » — le jour de la VALIDATION, heure de Paris. */
function leJour(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : `le ${QUAND.format(d)}`
}

/** « le 7 octobre 2026 par Hugo » — ce qui est connu, rien d'autre. */
function quandEtQui(c: NonNullable<PropositionValidation['current']>): string {
  return [leJour(c.validated_at), c.validated_by_name ? `par ${c.validated_by_name}` : ''].filter(Boolean).join(' ')
}

/**
 * « Validée sans clôture le 7 octobre 2026 par Hugo » — ou, pour une nuit
 * reprise de l'historique, d'où vient son chiffre : personne ne l'a validée
 * ici, et l'heure de l'import n'est pas celle d'un geste sur la nuit.
 */
function phraseValidee(c: NonNullable<PropositionValidation['current']>): string {
  const reprise = repriseDe(c.history)
  if (reprise !== null) return phraseReprise(reprise)
  const suite = quandEtQui(c)
  return `Validée sans clôture${suite ? ` ${suite}` : ''}`
}

/** « R-602927-001 » d'un bloc : coupée au tiret, la référence ne se relit plus. */
const insecable = (ref: string): string => ref.replace(/-/g, '\u2011')

/** La source des ventes Shotgun, dite en clair. */
function sourceVentes(r: PropositionValidation['revenue']): string {
  if (r.ticketing_source === 'REDDITION') {
    return r.reddition_refs.length > 0
      ? `Reddition ${r.reddition_refs.map(insecable).join(', ')} : total des ventes moins les frais gardés par Shotgun`
      : 'Reddition Shotgun : total des ventes moins les frais gardés par Shotgun'
  }
  if (r.ticketing_source === 'BILLETS') return 'D’après les billets valides, prix + 1 € de frais rendu par Shotgun : aucune reddition reçue'
  if (r.ticketing_source === 'REDDITION_ET_BILLETS') {
    return `Reddition${r.reddition_refs.length > 0 ? ` ${r.reddition_refs.map(insecable).join(', ')}` : ''} (total − frais gardés), et les billets (prix + 1 €) des soirées sans reddition`
  }
  if (r.ticketing_source === 'SANS_BILLET') return 'Aucun billet Shotgun valide pour cette nuit'
  return 'Il manque une reddition lue : à saisir'
}

/** Plafonds du serveur (`NUIT_VALIDEE_MAX_*`) : au-delà, c'est un zéro de trop. */
const MAX_ENTREES = 20_000
const MAX_CENTS = 1_000_000_000

/** Un nombre de personnes saisi : un entier de 0 au plafond, ou rien. */
function lireNombre(saisie: string): number | null {
  const t = saisie.trim()
  return /^\d{1,5}$/.test(t) && Number(t) <= MAX_ENTREES ? Number(t) : null
}

/* ═══════════════════════ le composant ═══════════════════════ */

export function IgnorerCloture(props: IgnorerClotureProps) {
  const { date, onDone, compact } = props
  const toast = useToast()
  const { permissions } = usePermissions()
  // Les droits du serveur (`finance-nights.ts`) : valider a l'effet d'une
  // finalisation, annuler celui d'une révision.
  const peutValider = permissions.includes('finance.closure.close')
  const peutAnnuler = permissions.includes('finance.closure.revise')

  const [prop, setProp] = useState<Etat<PropositionValidation>>({ etat: 'chargement' })
  const [geste, setGeste] = useState<'valider' | 'annuler' | null>(null)
  const [montant, setMontant] = useState<number | null>(null)
  const [entrees, setEntrees] = useState('')
  const [motif, setMotif] = useState('')
  const [occupe, setOccupe] = useState(false)

  const lire = useCallback(
    () => lireJson<PropositionValidation>(`/api/v1/finance/nights/${date}/validation-proposal`, commeProposition),
    [date],
  )
  const charger = useCallback(() => { void lire().then(setProp) }, [lire])
  useEffect(charger, [charger])

  if (prop.etat !== 'ok') return null
  const p = prop.valeur

  /**
   * RELIRE AVANT D'OUVRIR. Un rapport, une reddition ou une guestlist a pu
   * arriver depuis l'affichage (dans l'assistant, la guestlist se saisit
   * deux étapes plus loin) : c'est la proposition À JOUR qui préremplit les
   * champs, pas celle du chargement de la page.
   */
  const ouvrirValidation = async () => {
    setOccupe(true)
    const frais = await lire()
    setOccupe(false)
    // Une relecture manquée laisse le bouton en place, et le dit.
    if (frais.etat === 'absent') { toast('error', frais.message); return }
    if (frais.etat !== 'ok') return
    setProp(frais)
    if (frais.valeur.current !== null || frais.valeur.blocked_code !== null) return
    setMontant(frais.valeur.revenue.total_cents)
    setEntrees(String(frais.valeur.entries.total))
    setMotif('')
    setGeste('valider')
  }
  const fermer = () => { if (!occupe) setGeste(null) }

  const valider = async () => {
    const n = lireNombre(entrees)
    if (montant === null || n === null) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/nights/${date}/validation`,
      { revenue_cents: montant, entries: n, reason: motif.trim() === '' ? null : motif.trim() },
    )
    setOccupe(false)
    if (r.status >= 200 && r.status < 300 && r.data.error === undefined) {
      toast('ok', 'Nuit validée sans clôture détaillée : elle ne figure plus parmi les nuits à traiter.')
      setGeste(null)
      charger()
      onDone()
      return
    }
    toast('error', r.data.message ?? 'La nuit n’a pas pu être validée.')
  }

  const annuler = async () => {
    if (!motifSuffisant(motif)) return
    setOccupe(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/nights/${date}/validation/cancel`, { reason: motif.trim() },
    )
    setOccupe(false)
    if (r.status >= 200 && r.status < 300 && r.data.error === undefined) {
      toast('ok', 'Validation annulée : la nuit redevient à traiter.')
      setGeste(null)
      charger()
      onDone()
      return
    }
    toast('error', r.data.message ?? 'La validation n’a pas pu être annulée.')
  }

  /* ─── la nuit est déjà validée : on le dit, et on peut l'annuler ─── */
  if (p.current !== null) {
    const c = p.current
    const reprise = repriseDe(c.history)
    if (compact) {
      return (
        <span className="fe-valide fe-valide--compact" title={reprise?.label}>
          {reprise !== null
            ? <><History size={14} aria-hidden="true" /><span>{REPRIS_COURT}</span></>
            : <><BadgeCheck size={14} aria-hidden="true" /><span>Validée sans clôture</span></>}
        </span>
      )
    }
    return (
      <div className="fe-validee">
        <span className="fe-valide">
          {reprise !== null ? <History size={15} aria-hidden="true" /> : <BadgeCheck size={15} aria-hidden="true" />}
          <span>{phraseValidee(c)}</span>
        </span>
        {peutAnnuler ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setMotif(''); setGeste('annuler') }}>
            <RotateCcw size={14} aria-hidden="true" />Annuler la validation
          </button>
        ) : null}

        <Modal open={geste === 'annuler'} onClose={fermer} owning width={560} ariaLabel="Annuler la validation de la nuit">
          <header className="lc-modal__head">
            <span className="lc-modal__icon" aria-hidden="true"><RotateCcw size={22} /></span>
            <div>
              <div className="lc-modal__title">Annuler la validation</div>
              <div className="lc-modal__pills"><span className="lc-pill lc-pill--idle">{nuitLongue(date)}</span></div>
            </div>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={fermer} aria-label="Fermer">
              <X size={18} />
            </button>
          </header>
          <div className="lc-modal__body fe-modal">
            <p className="fe-modal__intro">
              La nuit redeviendra à traiter, et son chiffre validé sortira du chiffre d’affaires. La
              validation reste lisible dans le journal : rien n’est effacé.
            </p>
            <dl className="fe-recap">
              {/* Une nuit reprise n'a été validée par personne ici : « le 16 octobre
                  2026 par … » d'une nuit de 2017 serait faux — on dit sa source. */}
              {reprise !== null
                ? <div><dt>Provenance</dt><dd>{phraseReprise(reprise)}</dd></div>
                : <div><dt>Validée</dt><dd>{quandEtQui(c) || '—'}</dd></div>}
              <div><dt>Chiffre d’affaires validé</dt><dd className="fe-recap__montant">{formatMoney(c.revenue_cents)}</dd></div>
              {c.proposed_revenue_cents !== null && c.revenue_cents !== null && c.proposed_revenue_cents !== c.revenue_cents ? (
                <div><dt>Gestion proposait</dt><dd className="fe-recap__montant">{formatMoney(c.proposed_revenue_cents)}</dd></div>
              ) : null}
              <div><dt>Entrées validées</dt><dd>{nombre(c.entries)}</dd></div>
              {/* Le motif d'une reprise redit la provenance : déjà dite au-dessus. */}
              {c.reason && reprise === null ? <div><dt>Motif de la validation</dt><dd>« {c.reason} »</dd></div> : null}
            </dl>
            <MotifField
              id={`fe-annulation-${date}`}
              valeur={motif}
              onChange={setMotif}
              label="Pourquoi annuler cette validation ?"
              aide="Ce motif reste attaché à la nuit, dans le journal."
            />
            <div className="fiw-modal__actions">
              <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer} disabled={occupe}>Garder la validation</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={occupe || !motifSuffisant(motif)} onClick={() => void annuler()}>
                {occupe ? 'Annulation en cours' : 'Annuler la validation'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    )
  }

  /* ─── une vente du guichet d'une autre nuit attend sa décision : le geste
         existe, il est retenu, et la raison est écrite — l'écran ne la
         découvrait qu'au refus de l'envoi, formulaire rempli ─── */
  if (p.blocked_code === 'GUICHET_HORS_NUIT' && peutValider) {
    return (
      <>
        <button type="button" className={compact ? 'lc-btn lc-btn--ghost lc-btn--sm' : 'lc-btn'} disabled aria-describedby={`fe-guichet-${date}`}>
          <BadgeCheck size={compact ? 15 : 16} aria-hidden="true" />Ignorer la clôture
        </button>
        <span className="lc-hint" id={`fe-guichet-${date}`}>
          Une vente du guichet d’une autre nuit attend sa décision, à l’étape Accueil de la clôture : la nuit se validera ensuite.
        </span>
      </>
    )
  }

  /* ─── rien à offrir : nuit à venir, en cours, déjà clôturée, ou pas le droit ─── */
  if (p.blocked_code !== null || !peutValider) return null

  const r = p.revenue
  const n = lireNombre(entrees)
  const differe = montant !== null && r.total_cents !== null && montant !== r.total_cents
  const entreesDifferent = n !== null && n !== p.entries.total

  return (
    <>
      <button
        type="button"
        className={compact ? 'lc-btn lc-btn--ghost lc-btn--sm' : 'lc-btn'}
        disabled={occupe}
        onClick={() => void ouvrirValidation()}
      >
        <BadgeCheck size={compact ? 15 : 16} aria-hidden="true" />Ignorer la clôture
      </button>

      <Modal open={geste === 'valider'} onClose={fermer} owning width={760} ariaLabel="Ignorer la clôture de la nuit">
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true"><BadgeCheck size={22} /></span>
          <div>
            <div className="lc-modal__title">Ignorer la clôture : valider la nuit en bloc</div>
            <div className="lc-modal__pills"><span className="lc-pill lc-pill--idle">{nuitLongue(date)}</span></div>
          </div>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={fermer} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fe-modal">
          <p className="fe-modal__intro">
            La nuit ne sera plus à traiter : son chiffre d’affaires et ses entrées sont arrêtés en bloc,
            sans comptage point de vente par point de vente. À réserver aux nuits que Gestion n’a pas
            suivies — une clôture finalisée plus tard remplacera cette validation.
          </p>

          <div className="fe-blocs">
            <section className="fe-bloc" aria-label="Chiffre d’affaires proposé">
              <p className="fi-kpi__label">Chiffre d’affaires proposé</p>
              {r.amounts_visible ? (
                <dl className="fe-liste">
                  <div className="fe-ligne">
                    <dt>
                      <span className="fe-ligne__lib">Bars et vestiaire</span>
                      <span className="fe-ligne__sous">
                        {r.zap_reports === 0
                          ? 'Aucun rapport ZAP'
                          : `Annoncé par ZAP, hors offerts${r.zap_offers_cents ? ` (${formatMoney(r.zap_offers_cents)} d’offerts non comptés)` : ''}`}
                      </span>
                    </dt>
                    <dd className="fe-ligne__val">{formatMoney(r.zap_cents)}</dd>
                  </div>
                  <div className="fe-ligne">
                    <dt>
                      <span className="fe-ligne__lib">Ventes Shotgun · frais Shotgun déduits</span>
                      <span className="fe-ligne__sous">{sourceVentes(r)}</span>
                    </dt>
                    <dd className="fe-ligne__val">{formatMoney(r.ticketing_cents)}</dd>
                  </div>
                  {typeof r.associations_cents === 'number' ? (
                    <div className="fe-ligne">
                      <dt>
                        <span className="fe-ligne__lib">Billetterie des associations · Pretix</span>
                        <span className="fe-ligne__sous">Prix des billets vendus moins la part de chaque association</span>
                      </dt>
                      <dd className="fe-ligne__val">{formatMoney(r.associations_cents)}</dd>
                    </div>
                  ) : null}
                  <div className="fe-ligne fe-ligne--total">
                    <dt>
                      <span className="fe-ligne__lib">Total proposé</span>
                      {r.total_cents === null ? <span className="fe-ligne__sous">Une part manque : pas de total à proposer</span> : null}
                    </dt>
                    <dd className="fe-ligne__val">{formatMoney(r.total_cents)}</dd>
                  </div>
                </dl>
              ) : (
                <p className="lc-hint">
                  Votre rôle ne permet pas de lire les montants des rapports : le chiffre d’affaires se saisit
                  ci-dessous, sans proposition.
                </p>
              )}
              {r.amounts_visible && r.facial_cents !== null ? (
                // HORS du bloc chiffré et du total : un repère, sous son nom.
                <dl className="fe-liste fe-repere">
                  <div className="fe-ligne">
                    <dt>
                      <span className="fe-ligne__lib">Valeur faciale des billets</span>
                      <span className="fe-ligne__sous">Repère, pas un chiffre d’affaires : préventes et guichet, frais et remboursements non déduits</span>
                    </dt>
                    <dd className="fe-ligne__val">{formatMoney(r.facial_cents)}</dd>
                  </div>
                </dl>
              ) : null}
              {r.amounts_visible && typeof r.associations_face_cents === 'number' && r.associations_cents == null ? (
                // Réglage éteint : ce que les acheteurs ont payé sur Pretix, sous son nom, hors du total.
                <dl className="fe-liste fe-repere">
                  <div className="fe-ligne">
                    <dt>
                      <span className="fe-ligne__lib">Valeur faciale des billets Pretix</span>
                      <span className="fe-ligne__sous">Repère, hors du total : la billetterie des associations n’y entre pas (Comptabilité › Règles)</span>
                    </dt>
                    <dd className="fe-ligne__val">{formatMoney(r.associations_face_cents)}</dd>
                  </div>
                </dl>
              ) : null}
            </section>

            <section className="fe-bloc" aria-label="Entrées proposées">
              <p className="fi-kpi__label">Entrées proposées</p>
              <ListeEntrees e={p.entries} titreTotal="Total proposé" />
            </section>
          </div>

          {r.notes.length > 0 ? (
            <ul className="fe-notes">
              {r.notes.map((x) => <li key={x} className="fi-reserve">{x}</li>)}
            </ul>
          ) : null}

          <div className="fe-champs">
            <div className="fe-champ">
              <span className="lc-label">Chiffre d’affaires validé</span>
              <MoneyInput cents={montant} onChange={setMontant} ariaLabel="Chiffre d’affaires validé" />
              <span className="lc-helper">
                {montant === null
                  ? r.total_cents === null
                    ? 'À saisir d’après le rapport ZAP et les redditions Shotgun : Gestion n’a pas de total complet à proposer.'
                    : 'Saisissez le chiffre d’affaires total de la nuit.'
                  : montant > MAX_CENTS
                    ? `Au-delà de ${formatMoney(MAX_CENTS)} pour une nuit : vérifiez la saisie.`
                    : differe
                      ? `Différent de la proposition (${formatMoney(r.total_cents)}) : les deux restent enregistrés, dites pourquoi dans le motif.`
                      : r.total_cents === null
                        ? 'Saisi à la main : Gestion n’avait pas de total complet à proposer.'
                        : 'Bars, vestiaire et ventes Shotgun (frais Shotgun déduits), hors offerts.'}
              </span>
            </div>
            <div className="fe-champ">
              <label className="lc-label" htmlFor={`fe-entrees-${date}`}>Entrées validées</label>
              <input
                id={`fe-entrees-${date}`}
                className={`lc-input fe-nombre${entrees.trim() !== '' && n === null ? ' lc-input--error' : ''}`}
                inputMode="numeric"
                value={entrees}
                onChange={(e) => setEntrees(e.target.value)}
                aria-invalid={entrees.trim() !== '' && n === null}
              />
              <span className="lc-helper">
                {n === null
                  ? `Un nombre entier de personnes, de 0 à ${nombre(MAX_ENTREES)}.`
                  : entreesDifferent
                    ? `Différent de la proposition (${nombre(p.entries.total)}) : dites pourquoi dans le motif.`
                    : 'Billets Shotgun valides et guestlist.'}
              </span>
            </div>
          </div>

          <div className="fe-champ">
            <label className="lc-label" htmlFor={`fe-motif-${date}`}>Motif (facultatif)</label>
            <textarea
              id={`fe-motif-${date}`}
              className="lc-input fe-motif"
              rows={2}
              value={motif}
              maxLength={1000}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Par exemple : chiffre repris du classeur de la direction."
            />
          </div>

          <div className="fiw-modal__actions">
            <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer} disabled={occupe}>Annuler</button>
            <button type="button" className="lc-btn lc-btn--primary" disabled={occupe || montant === null || montant < 0 || montant > MAX_CENTS || n === null}
              onClick={() => void valider()}>
              <BadgeCheck size={16} aria-hidden="true" />{occupe ? 'Validation en cours' : 'Valider la nuit'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
